// src/hooks/sync/useSyncStore.js
// MOVED: hooks/useSyncStore.js → hooks/sync/useSyncStore.js  (Phase 6)
// IMPORT FIXES (all '../' relative paths → '@/' aliases):
//   '../lib/crypto'            → '@/lib/crypto'
//   '../contexts/PinContext'   → '@/contexts/PinContext'
//   '../lib/db/indexedDB'      → '@/lib/db/indexedDB'
//   '../lib/sync/syncManager'  → '@/lib/sync/syncManager'
//   '../lib/db/firestore'      → '@/lib/db/firestore'
//   '../lib/sync/onlineStatus' → '@/lib/sync/onlineStatus'
//   '../lib/storeKeys'         → '@/lib/storeKeys'
//   './useAutoCheckIn'         UNCHANGED — both files are now in hooks/sync/
//
// PHASE 5 CONSTANTS (this version):
//   WRITE_SUPPRESS_MS, SYNC_DEBOUNCE_MS, FIRESTORE_FALLBACK_TIMEOUT_MS moved
//   from inline literals to imports from @/config/app.constants. Values are
//   unchanged — this is a single-source-of-truth cleanup only.
//
// ── PREVIOUS PATCHES (retained) ──────────────────────────────────────────────
//   FIX P3: Debounced outbound Firestore writes per key.
//   FIX #18: Offline + null no longer hangs in an unresolved state.
//   FIX #4: initialValueRef kept in sync with initialValue prop on every render.
//   FIX #5: Firestore fallback timeout reduced 5000ms → 2000ms.
//   FIX #21 (race): loadingRef released after setStoredValue when IDB has data.
//
// ── THIS PATCH ───────────────────────────────────────────────────────────────
//   FIX OB-1 (P0): Onboarding loop — ProfileSetupScreen re-appears after
//     completeSetup() completes.
//
//     ROOT CAUSE: writingRef.current was cleared by a fixed WRITE_SUPPRESS_MS
//     timer that started at the moment of the local write. The Firestore write
//     is debounced by SYNC_DEBOUNCE_MS, so:
//
//       t=0      local write → writingRef=true, WRITE_SUPPRESS_MS timer starts
//       t=WRITE_SUPPRESS_MS  writingRef=false  ← gap opens
//       t=SYNC_DEBOUNCE_MS   Firestore write fires
//       t=SYNC_DEBOUNCE_MS+  Firestore onSnapshot fires with stale/null doc
//                            → setStoredValue(DEFAULT_PROFILE)
//                            → completedSetup=false → loop
//
//     For new accounts the Firestore document doesn't exist yet, so the
//     snapshot returns null → state resets to DEFAULT_PROFILE → AppContent
//     unmounts Dashboard and re-shows ProfileSetupScreen.
//
//     FIX: writingRef.current stays true until syncSet() Promise resolves
//     (or rejects), then a short WRITE_SUPPRESS_MS post-confirm buffer is
//     applied. The null snapshot that arrives while the write is in-flight is
//     always suppressed. By the time writingRef clears, Firestore has the
//     written document, so the next snapshot carries the correct data.
//
//     The delete path (valueToStore === null) receives the same treatment:
//     writingRef stays true until syncSet(null) confirms so a racing
//     Firestore snapshot can't restore a deleted record.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from 'react';
import { secureStorage }     from '@/lib/crypto';
import { usePin }            from '@/contexts/PinContext';
import { idbGet }            from '@/lib/db/storageAdapter';
import { syncSet, initSync } from '@/lib/sync/syncManager';
import { firestoreGet }      from '@/lib/db/firestore';
import { isOnline }          from '@/lib/sync/onlineStatus';
import {
  STORE_DAILY_ENTRIES,
  STORE_PEOPLE_CONTACTS,
  STORE_EXPENSES,
  STORE_WEEKLY_REPORTS,
  STORE_MONTHLY_REPORTS,
  STORE_CALENDAR_EVENTS,
} from '@/lib/storeKeys';
import {
  SYNC_DEBOUNCE_MS,
  WRITE_SUPPRESS_MS,
  FIRESTORE_FALLBACK_TIMEOUT_MS,
} from '@/config/app.constants';

// FIX TH-1: Serial gate for firestoreGet fallback calls.
//
// ROOT CAUSE of thundering herd: on every login, all useSyncStore instances
// mount simultaneously. Each one finds IDB empty (new device / fresh install)
// and fires firestoreGet() concurrently — 16+ parallel getDoc() calls race
// against the 2000ms timeout. The Firestore SDK queues or drops connections
// when too many simultaneous channels open, causing every call to hit the
// timeout. The console floods with "Firestore fallback failed" warnings.
//
// FIX: A module-level serial queue ensures only ONE firestoreGet runs at a
// time. Remaining callers wait their turn. Each individual call still has its
// own FIRESTORE_FALLBACK_TIMEOUT_MS timeout once it starts — the queue merely
// prevents them from all starting at the same instant.
//
// TRADE-OFF: Initial load on a brand-new device takes longer (16 × ~100ms
// sequential reads instead of 16 parallel ones). For returning users with IDB
// data this code path is never reached, so there is zero regression for the
// common case.
let _firestoreGetQueue = Promise.resolve();

function firestoreGetQueued(key, timeoutMs) {
  const result = _firestoreGetQueue.then(() => firestoreGet(key, timeoutMs));
  // Chain a no-throw tail so a single failure doesn't stall the whole queue.
  _firestoreGetQueue = result.then(() => {}, () => {});
  return result;
}

// FIX P3: Debounce outbound Firestore writes per key.
// Module-level Map persists across re-renders and is shared across all
// useSyncStore instances for the same key (consistent with the shared
// subscription cache in syncManager).
// TRADE-OFF: a crash within SYNC_DEBOUNCE_MS of the last keystroke loses
// that write from Firestore (IDB is already updated — re-syncs on next open).
const _writeDebounce = new Map(); // key -> { timeoutId, run }

// FIX FLUSH-1: Debounced writes (Firestore) and the dynamically-imported IDB
// write alongside them exist only in memory until their timer/import resolves.
// If the tab is closed or backgrounded before that happens, the pending write
// is silently dropped — on next load, neither IDB nor Firestore ever received
// it, so freshly-saved data (e.g. a just-configured reading plan) reverts to
// its default on reopen.
//
// FIX FLUSH-2: window.location.reload() (e.g. Layout.jsx's custom pull-to-
// refresh) begins tearing down the page immediately after the call returns —
// too fast for the visibilitychange/pagehide listeners below to reliably let
// an async IDB/Firestore write finish. Exporting flushPendingSyncWrites() lets
// call sites that trigger a reload themselves await the flush FIRST, which is
// the only reliable way to guarantee the write lands before navigation starts.
export function flushPendingSyncWrites() {
  const pending = [..._writeDebounce.entries()];
  for (const [k, entry] of pending) {
    clearTimeout(entry.timeoutId);
    _writeDebounce.delete(k);
  }
  return Promise.all(pending.map(([, entry]) => entry.run()));
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPendingSyncWrites();
  });
  window.addEventListener('pagehide', flushPendingSyncWrites);
}

// Sibling registry: every mounted useSyncStore instance for a key. A local write is pushed
// to the other instances so a long-lived instance (e.g. Layout) never applies an update
// to a stale copy before the Firestore snapshot round-trips (or while offline).
const _siblings = new Map(); // key -> Set<{ apply(value) }>

export function useSyncStore(key, initialValue) {
  const { activePin } = usePin();

  // FIX #4: Keep the ref updated on every render so async closures always
  // see the current initialValue, even if the parent passes a new instance.
  const initialValueRef = useRef(initialValue);
  initialValueRef.current = initialValue;

  const [storedValue, setStoredValue] = useState(initialValueRef.current);
  const [ready,       setReady]       = useState(false);

  const pinRef      = useRef(activePin);
  const prevPinRef  = useRef(activePin);

  const loadingRef      = useRef(false);
  const isMountedRef    = useRef(true);
  const writingRef      = useRef(false);
  const writeClearTimer = useRef(null);
  const idbHadDataRef   = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(writeClearTimer.current);
    };
  }, []);

  useEffect(() => { pinRef.current = activePin; }, [activePin]);

  // Register this instance so local writes from sibling instances reach it.
  const selfRef = useRef(null);
  useEffect(() => {
    const inst = {
      apply: (v) => {
        if (!isMountedRef.current) return;
        const iv = initialValueRef.current;
        setStoredValue(
          (Array.isArray(iv) && !Array.isArray(v) && v !== null && typeof v === 'object')
            ? Object.values(v)
            : v
        );
      },
    };
    selfRef.current = inst;
    let set = _siblings.get(key);
    if (!set) { set = new Set(); _siblings.set(key, set); }
    set.add(inst);
    return () => {
      set.delete(inst);
      if (set.size === 0) _siblings.delete(key);
    };
  }, [key]);

  // ── Load from IDB → secureStorage (migration) → Firestore ───────────────
  useEffect(() => {
    const prevPin      = prevPinRef.current;
    prevPinRef.current = activePin;

    const isUnlocking = prevPin === '' && activePin !== '';
    const isFirstLoad  = prevPin === activePin;
    if (!isFirstLoad && !isUnlocking) return;
    if (!isMountedRef.current) return;

    setReady(false);
    loadingRef.current    = true;
    idbHadDataRef.current = false;

    let cancelled = false;

    async function load() {
      const iv = initialValueRef.current;
      try {
        // 1. IndexedDB — fastest, works offline
        let value = await idbGet(key);
        if (cancelled) return;

        idbHadDataRef.current = (value !== null);

        // FIX #21: Only release loadingRef early when IDB returned null.
        // When IDB has data, keep loadingRef=true until setStoredValue fires
        // so an arriving Firestore snapshot cannot overwrite the IDB value.
        if (value === null) {
          loadingRef.current = false;
        }

        // 2. secureStorage — legacy migration path
        if (value === null) {
          try   { value = await secureStorage.get(key, activePin); }
          catch { value = null; }
          if (value !== null) await syncSet(key, value);
        }

        if (cancelled) return;

        // 3. Firestore fallback — new / additional devices (skip when offline)
        if (value === null && isOnline()) {
          try {
            value = await firestoreGetQueued(key, FIRESTORE_FALLBACK_TIMEOUT_MS);
            if (value !== null) await syncSet(key, value);
          } catch (err) {
            console.warn('[useSyncStore] Firestore fallback failed for', key, err);
          }
        }

        if (cancelled) return;

        if (isMountedRef.current) {
          if (value !== null) {
            // FIX iOS-IDB: WKWebView can deserialize a stored JS Array back as a
            // plain object { 0: ..., 1: ... } instead of a true Array. Any .map()
            // call on that object throws "X.map is not a function" at runtime.
            // When the initial value for this store is an array, coerce the loaded
            // value back to an array using Object.values() as a recovery path.
            // Array.isArray short-circuits the common (correct) case with zero cost.
            const coerced = (Array.isArray(iv) && !Array.isArray(value) && typeof value === 'object')
              ? Object.values(value)
              : value;
            setStoredValue(coerced);
            loadingRef.current = false; // FIX #21: release AFTER setting
          } else {
            setStoredValue(iv);
          }
        }
      } catch (err) {
        console.error('[useSyncStore] load error for', key, err);
        loadingRef.current = false;
        if (isMountedRef.current && !cancelled) setStoredValue(initialValueRef.current);
      } finally {
        loadingRef.current = false;
        if (isMountedRef.current && !cancelled) setReady(true);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [key, activePin]);

  // ── Real-time inbound sync ────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = initSync(key, (remoteValue) => {
      if (loadingRef.current && idbHadDataRef.current) return;
      if (writingRef.current)    return;
      if (!isMountedRef.current) return;
      // FIX iOS-IDB: same coercion as the load path — Firestore can also
      // return a plain object when the stored value was originally an array.
      const iv = initialValueRef.current;
      const raw = remoteValue !== null ? remoteValue : iv;
      const coerced = (Array.isArray(iv) && !Array.isArray(raw) && raw !== null && typeof raw === 'object')
        ? Object.values(raw)
        : raw;
      setStoredValue(coerced);
    });
    return unsubscribe;
  }, [key]);

  // ── Write ─────────────────────────────────────────────────────────────────
  // FIX OB-1: writingRef now stays true until syncSet() resolves, then a
  // WRITE_SUPPRESS_MS post-confirm buffer is applied. Previously writingRef
  // was cleared by a fixed timer starting at write time, which expired before
  // the debounced Firestore write completed — leaving a gap where a stale/null
  // Firestore snapshot could overwrite local state and reset onboarding.
  //
  // Helper: schedule the post-confirm clearance of writingRef.
  // Defined outside setValue so it's reusable by both branches.
  const scheduleWriteClear = useCallback(() => {
    clearTimeout(writeClearTimer.current);
    writeClearTimer.current = setTimeout(() => {
      writingRef.current = false;
    }, WRITE_SUPPRESS_MS);
  }, []); // no deps — only touches refs and a constant

  const setValue = useCallback((value) => {
    setStoredValue((prev) => {
      const iv           = initialValueRef.current;
      const valueToStore = value instanceof Function ? value(prev) : value;

      // Mark as writing immediately so inbound snapshots are suppressed
      // from this point until the Firestore write confirms + WRITE_SUPPRESS_MS.
      writingRef.current = true;
      clearTimeout(writeClearTimer.current);

      if (valueToStore === null) {
        // DELETE — always immediate, no debounce.
        clearTimeout(_writeDebounce.get(key)?.timeoutId);
        _writeDebounce.delete(key);
        syncSet(key, null)
          .then(() => scheduleWriteClear())
          .catch((err) => {
            console.error('[useSyncStore] syncSet(null) error:', key, err);
            // Still clear writingRef on failure — don't block reads forever.
            writingRef.current = false;
          });
        // FIX (Flag 29): previously fired bare/unawaited with no error
        // handling — asymmetric with the write path's secureStorage.set(),
        // which IS inside the Promise.all().catch() below. Now a failure
        // here is logged instead of silently swallowed / an unhandled
        // rejection, without changing the fire-and-forget timing.
        if (pinRef.current) {
          Promise.resolve(secureStorage.remove(key)).catch((err) => {
            console.error('[useSyncStore] secureStorage.remove error:', key, err);
          });
        }
        return iv;
      }

      // FIX P3: IDB immediate, Firestore debounced.
      // FIX OB-1: writingRef cleared only after the debounced syncSet resolves.
      // FIX FLUSH-1: the debounce entry now stores { timeoutId, run } so
      // flushAllPendingWrites() (visibilitychange/pagehide) can execute this
      // same persist logic immediately if the app closes before the timer fires.
      clearTimeout(_writeDebounce.get(key)?.timeoutId);
      const runPersist = () => {
        _writeDebounce.delete(key);
        // FIX (Flag 32): skipIdbWrite:true — the immediate idbSetDirect() call
        // below already wrote this value to IDB; syncSet() doesn't need to.
        const persistPromises = [syncSet(key, valueToStore, { skipIdbWrite: true })];
        if (pinRef.current) {
          persistPromises.push(secureStorage.set(key, valueToStore, pinRef.current));
        }
        // FIX FLUSH-2: return the chain so flushPendingSyncWrites() callers
        // (e.g. a manual reload) can await real completion, not just kick it off.
        return Promise.all(persistPromises)
          .then(() => scheduleWriteClear())
          .catch((err) => {
            console.error('[useSyncStore] persist error:', key, err);
            // Still clear writingRef on failure — don't block reads forever.
            writingRef.current = false;
          });
      };
      const timeoutId = setTimeout(runPersist, SYNC_DEBOUNCE_MS);
      _writeDebounce.set(key, { timeoutId, run: runPersist });

      // Push the new value to sibling instances of this key (deferred: never update
      // another component's state while this updater runs during render).
      const self = selfRef.current;
      Promise.resolve().then(() => {
        _siblings.get(key)?.forEach((inst) => { if (inst !== self) inst.apply(valueToStore); });
      });

      // Immediate IDB write keeps component consistent during debounce window.
      import('@/lib/db/storageAdapter').then(({ idbSet: idbSetDirect }) => {
        idbSetDirect(key, valueToStore).catch(() => {});
      });

      return valueToStore;
    });
  }, [key, scheduleWriteClear]);

  return [storedValue, setValue, ready];
}

// ── Convenience hook for journal-related stores ───────────────────────────────
export function useJournalData() {
  const [dailyEntries,   setDailyEntries,   dailyEntriesReady]   = useSyncStore(STORE_DAILY_ENTRIES,   {});
  const [peopleContacts, setPeopleContacts, peopleContactsReady] = useSyncStore(STORE_PEOPLE_CONTACTS, []);
  const [expenses,       setExpenses,       expensesReady]       = useSyncStore(STORE_EXPENSES,        []);
  const [weeklyReports,  setWeeklyReports,  weeklyReportsReady]  = useSyncStore(STORE_WEEKLY_REPORTS,  []);
  const [monthlyReports, setMonthlyReports, monthlyReportsReady] = useSyncStore(STORE_MONTHLY_REPORTS, []);
  const [calendarEvents, setCalendarEvents, calendarEventsReady] = useSyncStore(STORE_CALENDAR_EVENTS, []);

  return {
    dailyEntries,   setDailyEntries,   dailyEntriesReady,
    peopleContacts, setPeopleContacts, peopleContactsReady,
    expenses,       setExpenses,       expensesReady,
    weeklyReports,  setWeeklyReports,  weeklyReportsReady,
    monthlyReports, setMonthlyReports, monthlyReportsReady,
    calendarEvents, setCalendarEvents, calendarEventsReady,
  };
}

// ── DEPRECATED ────────────────────────────────────────────────────────────────
// @deprecated Use useSyncStore directly. This alias exists only for backward
// compatibility with existing callsites and will be removed in a future cleanup.
// 🟡 WARNING: useLocalStorage does NOT touch window.localStorage — it writes
// to IndexedDB and syncs to Firestore. Do not use for new code.
export const useLocalStorage = useSyncStore;

// ── MISPLACED RE-EXPORT — REMOVED (Phase 6 callsite cleanup complete) ────────
// All callers have been updated to import directly from '@/hooks/sync/useAutoCheckIn'.
// The re-export block below has been removed to eliminate the circular-ish
// dependency and make the module boundary explicit.
//
// If you still see an import error for useAutoCheckIn from useSyncStore,
// update the import path to: '@/hooks/sync/useAutoCheckIn'